import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

export type PillMode = "waiting" | "connected" | "torn-down" | "error";

function compactAgo(iso: string, now: number): string {
  const secs = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.round(mins / 60)}h ago`;
}

// Small status pill reflecting the live link with the member's terminal, using
// the app's green (connected) / amber (reconnecting) / muted (waiting) vocabulary.
export function ConnectionPill({
  mode,
  lastCheckAt,
}: {
  mode: PillMode;
  lastCheckAt?: string;
}) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (mode !== "connected" || !lastCheckAt) return;
    const id = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(id);
  }, [mode, lastCheckAt]);

  const base =
    "inline-flex items-center gap-2 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium";
  const green =
    "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950/30 dark:text-green-400";
  const amber =
    "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-400";
  const muted = "border-border bg-muted text-muted-foreground";

  const dot = (color: string) => (
    <span className={cn("size-1.5 rounded-full", color)} />
  );

  if (mode === "waiting") {
    return (
      <span className={cn(base, muted)}>
        {dot("bg-muted-foreground/60")}CLI not connected
      </span>
    );
  }
  if (mode === "error") {
    return (
      <span className={cn(base, amber)}>
        {dot("bg-amber-500")}Reconnecting…
      </span>
    );
  }
  if (mode === "torn-down") {
    return (
      <span className={cn(base, green)}>
        {dot("bg-green-500")}Environment torn down
      </span>
    );
  }
  return (
    <span className={cn(base, green)}>
      {dot("bg-green-500")}CLI connected
      {lastCheckAt ? ` · last check ${compactAgo(lastCheckAt, now)}` : ""}
    </span>
  );
}
